import { useState } from 'react';
import {
  Loader2,
  Sparkles,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  HandCoins,
  PieChart,
} from 'lucide-react';
import Button from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import Badge from './ui/Badge';
import api from '../lib/api';

export interface JobPricingReport {
  executiveSummary: string;
  budgetHealth: {
    score: number;
    status: 'underspent' | 'balanced' | 'overspent' | 'insufficient';
    summary: string;
  };
  marketBenchmarks: Array<{ tier: string; platform: string; rateRangeRM: string; note: string }>;
  kolPricingFlags: Array<{
    kolName: string;
    status: 'competitive' | 'overpriced' | 'underpriced';
    suggestedRangeRM: string;
    acceptanceLikelihood: 'high' | 'medium' | 'low';
  }>;
  budgetScenarios: Array<{
    label: string;
    totalRM: number;
    kolCount: number;
    expectedReach: string;
    roiEstimate: string;
  }>;
  negotiationTips: string[];
  recommendations: string[];
  demo?: boolean;
  cached?: boolean;
}

export interface BudgetPlanResult {
  narrative: string;
  totalAllocated: number;
  remaining: number;
  allocations: Array<{
    kolProfileId: string;
    kolName: string;
    platform: string;
    deliverables: string;
    estimatedCost: number;
    rationale: string;
  }>;
  risks: string[];
  alternatives: string[];
  demo?: boolean;
  cached?: boolean;
}

export interface KOLRateSuggestion {
  kolName: string;
  platform: string;
  marketRangeRM: { low: number; mid: number; high: number };
  recommendedOfferRM: number;
  acceptanceLikelihood: 'high' | 'medium' | 'low';
  negotiationTips: string[];
  incentiveIdeas: string[];
  demo?: boolean;
  cached?: boolean;
}

interface KOLOption {
  id: string;
  name: string;
}

interface AIPricingPanelProps {
  jobId: string;
  totalBudgetRM?: number;
  kols?: KOLOption[];
  title?: string;
  className?: string;
}

function healthVariant(status: string): 'success' | 'warning' | 'destructive' | 'secondary' {
  if (status === 'balanced') return 'success';
  if (status === 'overspent') return 'destructive';
  if (status === 'insufficient') return 'warning';
  return 'secondary';
}

function pricingFlagVariant(status: string): 'success' | 'warning' | 'destructive' {
  if (status === 'competitive') return 'success';
  if (status === 'underpriced') return 'warning';
  return 'destructive';
}

function likelihoodVariant(l: string): 'success' | 'warning' | 'secondary' {
  if (l === 'high') return 'success';
  if (l === 'medium') return 'warning';
  return 'secondary';
}

export function AIPricingPanel({
  jobId,
  totalBudgetRM,
  kols = [],
  title = 'Dynamic Pricing & Budget Optimization',
  className = '',
}: AIPricingPanelProps) {
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [rateLoading, setRateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<JobPricingReport | null>(null);
  const [plan, setPlan] = useState<BudgetPlanResult | null>(null);
  const [rateSuggestion, setRateSuggestion] = useState<KOLRateSuggestion | null>(null);
  const [constraints, setConstraints] = useState('');
  const [selectedKolId, setSelectedKolId] = useState(kols[0]?.id ?? '');

  const runJobAnalysis = async () => {
    setAnalysisLoading(true);
    setError(null);
    try {
      const res = await api.post('/ai/pricing/job-analysis', { jobId });
      setReport(res.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to analyze job pricing';
      setError(msg);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const runBudgetPlan = async () => {
    setPlanLoading(true);
    setError(null);
    try {
      const res = await api.post('/ai/budget/plan', { jobId, constraints: constraints || undefined });
      setPlan(res.data.plan);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to generate budget plan';
      setError(msg);
    } finally {
      setPlanLoading(false);
    }
  };

  const runRateSuggestion = async () => {
    if (!selectedKolId) {
      setError('Select a KOL first');
      return;
    }
    setRateLoading(true);
    setError(null);
    try {
      const res = await api.post('/ai/pricing/kol-rate', { jobId, kolProfileId: selectedKolId });
      setRateSuggestion(res.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to suggest KOL rate';
      setError(msg);
    } finally {
      setRateLoading(false);
    }
  };

  const isDemo = report?.demo || plan?.demo || rateSuggestion?.demo;
  const isCached = report?.cached || plan?.cached || rateSuggestion?.cached;

  return (
    <Card className={`border-amber-200/60 bg-amber-50/30 dark:bg-amber-950/20 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <DollarSign className="h-5 w-5 text-amber-600" />
          {title}
        </CardTitle>
        <CardDescription>
          Market benchmarks, budget allocation, and KOL rate negotiation — works in demo mode without DeepSeek.
          {totalBudgetRM != null && ` Job budget: RM ${totalBudgetRM.toLocaleString()}.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={runJobAnalysis} disabled={analysisLoading} size="sm" variant="outline">
            {analysisLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <TrendingUp className="h-4 w-4 mr-2" />
                Analyze Job Pricing
              </>
            )}
          </Button>
          {isDemo && <Badge variant="outline">Demo mode</Badge>}
          {isCached && <Badge variant="outline">Cached</Badge>}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {report && (
          <div className="space-y-4 rounded-lg border bg-white/60 dark:bg-black/20 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-3xl font-bold text-amber-600">{report.budgetHealth.score}</div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">Budget health</p>
                  <Badge variant={healthVariant(report.budgetHealth.status)}>
                    {report.budgetHealth.status}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{report.executiveSummary}</p>
                <p className="text-xs text-gray-500 mt-1">{report.budgetHealth.summary}</p>
              </div>
            </div>

            {report.marketBenchmarks.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Market benchmarks</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b">
                        <th className="pb-2 pr-3">Tier</th>
                        <th className="pb-2 pr-3">Platform</th>
                        <th className="pb-2 pr-3">Rate range</th>
                        <th className="pb-2">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.marketBenchmarks.map((b, i) => (
                        <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-2 pr-3">{b.tier}</td>
                          <td className="py-2 pr-3">{b.platform}</td>
                          <td className="py-2 pr-3 font-medium">{b.rateRangeRM}</td>
                          <td className="py-2 text-gray-600">{b.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {report.kolPricingFlags.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">KOL pricing flags</p>
                <div className="space-y-2">
                  {report.kolPricingFlags.map((f, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 text-sm rounded-lg border p-3">
                      <span className="font-medium">{f.kolName}</span>
                      <Badge variant={pricingFlagVariant(f.status)}>{f.status}</Badge>
                      <span className="text-gray-600">{f.suggestedRangeRM}</span>
                      <Badge variant={likelihoodVariant(f.acceptanceLikelihood)}>
                        {f.acceptanceLikelihood} acceptance
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.budgetScenarios.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                  <PieChart className="h-4 w-4" /> Budget scenarios
                </p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {report.budgetScenarios.map((s, i) => (
                    <div key={i} className="rounded-lg border p-3 text-sm">
                      <p className="font-medium">{s.label}</p>
                      <p className="text-amber-700 dark:text-amber-300">RM {s.totalRM.toLocaleString()}</p>
                      <p className="text-gray-600">{s.kolCount} KOLs · {s.expectedReach} reach</p>
                      <p className="text-xs text-gray-500 mt-1">{s.roiEstimate}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.negotiationTips.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">Negotiation tips</p>
                <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300">
                  {report.negotiationTips.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            )}

            {report.recommendations.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-1">
                  <Sparkles className="h-4 w-4" /> Recommendations
                </p>
                <ul className="list-disc list-inside text-sm text-amber-800 dark:text-amber-200">
                  {report.recommendations.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="border-t pt-6 space-y-3">
          <p className="text-sm font-medium flex items-center gap-1">
            <PieChart className="h-4 w-4" /> AI Budget Planner
          </p>
          <p className="text-xs text-gray-600">
            Allocate budget across shortlisted KOLs based on rate cards and constraints.
          </p>
          <textarea
            className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm min-h-[72px]"
            placeholder="Optional constraints, e.g. minimum 3 IG Reels, max RM 2000 per KOL..."
            value={constraints}
            onChange={(e) => setConstraints(e.target.value)}
          />
          <Button onClick={runBudgetPlan} disabled={planLoading} size="sm">
            {planLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Planning...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Budget Plan
              </>
            )}
          </Button>

          {plan && (
            <div className="space-y-3 rounded-lg border p-4 text-sm">
              {plan.narrative && (
                <p className="text-gray-700 dark:text-gray-300">{plan.narrative}</p>
              )}
              {plan.allocations.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b">
                        <th className="pb-2 pr-3">KOL</th>
                        <th className="pb-2 pr-3">Platform</th>
                        <th className="pb-2 pr-3">Deliverables</th>
                        <th className="pb-2 pr-3">Cost</th>
                        <th className="pb-2">Rationale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.allocations.map((a, i) => (
                        <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-2 pr-3 font-medium">{a.kolName}</td>
                          <td className="py-2 pr-3">{a.platform}</td>
                          <td className="py-2 pr-3">{a.deliverables}</td>
                          <td className="py-2 pr-3">RM {a.estimatedCost.toLocaleString()}</td>
                          <td className="py-2 text-gray-600">{a.rationale}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex gap-4">
                <span className="font-medium">Total: RM {plan.totalAllocated.toLocaleString()}</span>
                <span className="text-gray-600">Remaining: RM {plan.remaining.toLocaleString()}</span>
              </div>
              {plan.risks.length > 0 && (
                <div className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <ul className="list-disc list-inside">
                    {plan.risks.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {kols.length > 0 && (
          <div className="border-t pt-6 space-y-3">
            <p className="text-sm font-medium flex items-center gap-1">
              <HandCoins className="h-4 w-4" /> KOL Rate Suggestion
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={selectedKolId}
                onChange={(e) => setSelectedKolId(e.target.value)}
              >
                {kols.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
              <Button onClick={runRateSuggestion} disabled={rateLoading} size="sm" variant="outline">
                {rateLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Suggesting...
                  </>
                ) : (
                  'Suggest Rate'
                )}
              </Button>
            </div>

            {rateSuggestion && (
              <div className="rounded-lg border p-4 text-sm space-y-2">
                <p className="font-medium">
                  {rateSuggestion.kolName} · {rateSuggestion.platform}
                </p>
                <p>
                  Market range: RM {rateSuggestion.marketRangeRM.low.toLocaleString()} –{' '}
                  {rateSuggestion.marketRangeRM.high.toLocaleString()} (mid RM{' '}
                  {rateSuggestion.marketRangeRM.mid.toLocaleString()})
                </p>
                <p className="text-lg font-semibold text-amber-700 dark:text-amber-300">
                  Recommended offer: RM {rateSuggestion.recommendedOfferRM.toLocaleString()}
                </p>
                <Badge variant={likelihoodVariant(rateSuggestion.acceptanceLikelihood)}>
                  {rateSuggestion.acceptanceLikelihood} acceptance likelihood
                </Badge>
                {rateSuggestion.negotiationTips.length > 0 && (
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                    {rateSuggestion.negotiationTips.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                )}
                {rateSuggestion.incentiveIdeas.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Incentive ideas</p>
                    <ul className="list-disc list-inside text-gray-600">
                      {rateSuggestion.incentiveIdeas.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
